import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import MemberProfileTimelineClass from "@/components/Provider/PatientView/MemberProfile/MemberProfileTimeline";
import type { PatientDetails, UUID } from "@/core/types";

import { timelineKeys } from "./api/keyFactory";
import { useActivityFeed } from "./api/queries/useActivityFeed";
import { usePracticeName } from "./api/queries/usePracticeName";

type Props = {
  patientDetails: PatientDetails;
  refetchPatientDetails: () => void;
};

export function MemberProfileTimeline({ patientDetails, refetchPatientDetails }: Props) {
  const { patientId } = useParams<{ patientId: UUID }>();
  const queryClient = useQueryClient();
  const waitlistChangeCount = useSelector(
    (state: { patient: { waitlistChangeCount: number } }) => state.patient.waitlistChangeCount,
  );
  const prevCountRef = useRef<number>(waitlistChangeCount);

  const { data: activities = [] } = useActivityFeed({ patientId });
  const { data: practiceName } = usePracticeName({
    practiceId: patientDetails?.pcp_practice_id,
  });

  // Invalidate the activity feed when the waitlist count changes (not on mount)
  useEffect(() => {
    if (prevCountRef.current === waitlistChangeCount) return;
    prevCountRef.current = waitlistChangeCount;
    queryClient.invalidateQueries({ queryKey: timelineKeys.feed(patientId) });
  }, [waitlistChangeCount, queryClient, patientId]);

  return (
    <MemberProfileTimelineClass
      data={{ activities, practiceName }}
      patientDetails={patientDetails}
      refetchPatientDetails={refetchPatientDetails}
    />
  );
}
